import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';
import { toast } from 'react-toastify';

interface RecordedSessionsProps {
    serviceProviderId: string;
    maxItems?: number; // Optional limit for showing recent recordings
}

interface RecordingDocument {
    id: string;
    created_at: any; // Firestore timestamp
    created_by: string;
    filename: string;
    layout: string;
    meeting_title: string;
    original_room_name: string;
    recording_id: string;
    recording_type: string;
    resolution: string;
    room_name: string;
    s3_bucket: string;
    s3_key: string;
    started_at: string;
    status: string;
    stop_response?: string;
    stopped_at: any; // Firestore timestamp
    tenant_id: string;
    updated_at: any; // Firestore timestamp
    playback_url?: string; // Transcoded URL for playback
}

const RecordedSessions: React.FC<RecordedSessionsProps> = ({
    serviceProviderId,
    maxItems
}) => {
    const [recordings, setRecordings] = useState<RecordingDocument[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRecording, setSelectedRecording] = useState<RecordingDocument | null>(null);
    const [showPlayerModal, setShowPlayerModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState<'all' | 'stopped' | 'processing'>('all');

    // Fetch recordings from Firestore in real-time
    useEffect(() => {
        if (!serviceProviderId) return;

        setIsLoading(true);

        const recordingsRef = collection(db, 'ServiceProviders', serviceProviderId, 'Recordings');
        const q = query(recordingsRef, orderBy('created_at', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const recordingsData: RecordingDocument[] = [];

            snapshot.forEach((doc) => {
                const data = doc.data();
                recordingsData.push({
                    id: doc.id,
                    created_at: data.created_at,
                    created_by: data.created_by,
                    filename: data.filename,
                    layout: data.layout,
                    meeting_title: data.meeting_title,
                    original_room_name: data.original_room_name,
                    recording_id: data.recording_id,
                    recording_type: data.recording_type,
                    resolution: data.resolution,
                    room_name: data.room_name,
                    s3_bucket: data.s3_bucket,
                    s3_key: data.s3_key,
                    started_at: data.started_at,
                    status: data.status,
                    stop_response: data.stop_response,
                    stopped_at: data.stopped_at,
                    tenant_id: data.tenant_id,
                    updated_at: data.updated_at,
                    playback_url: `https://${data.s3_bucket}.s3.amazonaws.com/${data.s3_key}` // Construct playback URL
                });
            });

            setRecordings(recordingsData);
            setIsLoading(false);
        }, (error) => {
            console.error('Error fetching recordings:', error);
            toast.error('Failed to load recordings');
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [serviceProviderId]);

    const deleteRecording = async (recordingId: string, recordingTitle: string) => {
        if (!confirm(`Are you sure you want to delete "${recordingTitle}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await deleteDoc(doc(db, 'ServiceProviders', serviceProviderId, 'Recordings', recordingId));
            toast.success('Recording deleted successfully!');
        } catch (error) {
            console.error('Error deleting recording:', error);
            toast.error('Failed to delete recording');
        }
    };

    const playRecording = (recording: RecordingDocument) => {
        setSelectedRecording(recording);
        setShowPlayerModal(true);
    };

    const formatDuration = (startTime: string, stopTime: any) => {
        try {
            const start = new Date(startTime);
            const stop = stopTime?.toDate ? stopTime.toDate() : new Date(stopTime);
            const diffMs = stop.getTime() - start.getTime();
            const diffMins = Math.floor(diffMs / 60000);
            const hours = Math.floor(diffMins / 60);
            const mins = diffMins % 60;
            return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
        } catch {
            return 'Unknown';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'stopped': return 'bg-green-100 text-green-800';
            case 'recording': return 'bg-red-100 text-red-800';
            case 'processing': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'stopped': return 'Ready';
            case 'recording': return 'Recording';
            case 'processing': return 'Processing';
            default: return status;
        }
    };

    const filteredRecordings = recordings.filter(recording => {
        if (filterStatus === 'all') return true;
        if (filterStatus === 'stopped') return recording.status === 'stopped';
        if (filterStatus === 'processing') return recording.status !== 'stopped';
        return true;
    });

    // Apply maxItems limit if specified
    const displayRecordings = maxItems ? filteredRecordings.slice(0, maxItems) : filteredRecordings;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                            <polygon points="23 7 16 12 23 17 23 7"></polygon>
                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Recorded Sessions</h3>
                        <p className="text-sm text-gray-600">View and manage your recorded Meet+ sessions</p>
                    </div>
                </div>

                {/* Filter - Only show when not limited */}
                {!maxItems && (
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Filter:</span>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value as any)}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        >
                            <option value="all">All Recordings ({recordings.length})</option>
                            <option value="stopped">Ready to Watch ({recordings.filter(r => r.status === 'stopped').length})</option>
                            <option value="processing">Processing ({recordings.filter(r => r.status !== 'stopped').length})</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Recordings List */}
            <div className="space-y-3">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                        <span className="ml-3 text-gray-600">Loading recordings...</span>
                    </div>
                ) : filteredRecordings.length === 0 ? (
                    <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                            </svg>
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No recordings found</h4>
                        <p className="text-gray-600 mb-4">
                            {filterStatus === 'all' 
                                ? 'Start recording your meetings to see them here.' 
                                : `No recordings with this status available.`
                            }
                        </p>
                        {filterStatus !== 'all' && (
                            <button
                                onClick={() => setFilterStatus('all')}
                                className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                            >
                                View all recordings
                            </button>
                        )}
                    </div>
                ) : (
                    displayRecordings.map((recording) => (
                        <div key={recording.id} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                        <h4 className="font-semibold text-gray-900 text-lg">{recording.meeting_title}</h4>
                                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(recording.status)}`}>
                                            {getStatusLabel(recording.status)}
                                        </span>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-sm mb-3">
                                        <div>
                                            <span className="text-gray-500 block mb-1">Created By</span>
                                            <span className="font-medium text-gray-900">{recording.created_by}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Duration</span>
                                            <span className="font-medium text-gray-900">
                                                {formatDuration(recording.started_at, recording.stopped_at)}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Resolution</span>
                                            <span className="font-medium text-gray-900">{recording.resolution}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Layout</span>
                                            <span className="font-medium text-gray-900 capitalize">
                                                {recording.layout.replace('-', ' ')}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Started</span>
                                            <span className="font-medium text-gray-900">
                                                {new Date(recording.started_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Time</span>
                                            <span className="font-medium text-gray-900">
                                                {new Date(recording.started_at).toLocaleTimeString()}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Recording ID</span>
                                            <span className="font-medium text-gray-900 font-mono text-xs">
                                                {recording.recording_id}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Type</span>
                                            <span className="font-medium text-gray-900 capitalize">
                                                {recording.recording_type}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-2 text-xs text-gray-600">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                                <circle cx="12" cy="10" r="3"></circle>
                                            </svg>
                                            <span className="font-mono text-xs break-all">{recording.filename}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="flex flex-col items-end gap-2 ml-4">
                                    {recording.status === 'stopped' && (
                                        <button
                                            onClick={() => playRecording(recording)}
                                            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg hover:from-purple-600 hover:to-blue-700 transition-all flex items-center gap-2 shadow-md"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                            </svg>
                                            Play
                                        </button>
                                    )}
                                    <button
                                        onClick={() => deleteRecording(recording.id, recording.meeting_title)}
                                        className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 border border-red-200"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        </svg>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* View More Link - Removed in favor of custom button in parent */}

            {/* Video Player Modal */}
            {showPlayerModal && selectedRecording && (
                <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-6xl mx-4 shadow-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-900">
                                {selectedRecording.meeting_title}
                            </h3>
                            <button
                                onClick={() => setShowPlayerModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 6L6 18M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>

                        <div className="aspect-video bg-black rounded-lg overflow-hidden shadow-xl">
                            {selectedRecording.playback_url ? (
                                <video
                                    controls
                                    autoPlay
                                    className="w-full h-full"
                                    preload="metadata"
                                >
                                    <source src={selectedRecording.playback_url} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <div className="flex items-center justify-center h-full text-white">
                                    <div className="text-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4">
                                            <polygon points="23 7 16 12 23 17 23 7"></polygon>
                                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                        </svg>
                                        <p className="text-xl font-medium mb-2">Video not available</p>
                                        <p className="text-sm text-gray-300">Recording file is still processing or not available.</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <span className="text-gray-500 block mb-1">Duration</span>
                                <span className="font-semibold text-gray-900">
                                    {formatDuration(selectedRecording.started_at, selectedRecording.stopped_at)}
                                </span>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <span className="text-gray-500 block mb-1">Resolution</span>
                                <span className="font-semibold text-gray-900">{selectedRecording.resolution}</span>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <span className="text-gray-500 block mb-1">Created By</span>
                                <span className="font-semibold text-gray-900">{selectedRecording.created_by}</span>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <span className="text-gray-500 block mb-1">Date</span>
                                <span className="font-semibold text-gray-900">
                                    {new Date(selectedRecording.started_at).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecordedSessions;

